import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowLeft, Phone, KeyRound } from "lucide-react";
import { apiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import Logo from "@/components/Logo";

const Login = () => {
  const nav = useNavigate();
  const { login, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("+91");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [referral, setReferral] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/app", { replace: true }); }, [user, nav]);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferral(ref.toUpperCase());
  }, [searchParams]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!phone || phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone");
    if (!acceptTerms) return toast.error("Please accept the Terms of Service and Privacy Policy to continue");
    setBusy(true);
    try {
      const r = await api.post("/auth/request-otp", { phone });
      setDevOtp(r.data.dev_otp || "");
      setIsNew(!!r.data.is_new);
      setStep(2);
      toast.success(r.data.dev_otp ? "OTP sent · Dev OTP shown below" : "OTP sent to your phone");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    }
    setBusy(false);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) return toast.error("Enter 6-digit OTP");
    if (isNew && (!firstName.trim() || !lastName.trim())) return toast.error("Please enter your first and last name");
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-otp", {
        phone,
        otp,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        referral_code: referral.trim().toUpperCase() || undefined,
        accept_terms: acceptTerms,
        accept_privacy: acceptTerms,
      });
      login(r.data.token, r.data.user);
      toast.success("Welcome to CarryIt");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err, "Invalid OTP"));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 grid-bg flex items-center">
      <div className="max-w-md mx-auto w-full px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-6"><ArrowLeft className="w-4 h-4"/> Home</Link>
        <div className="rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
          <div className="flex flex-col items-center text-center mb-6">
            <Logo size={56} variant="dark"/>
            <span className="mt-3 font-display text-xl font-bold">CarryIt</span>
          </div>
          {step === 1 && (
            <>
              <h1 className="font-display text-2xl font-extrabold">Enter your phone</h1>
              <p className="text-slate-500 text-sm mt-1">We&apos;ll send you a 6-digit code.</p>
              <form onSubmit={sendOtp} className="mt-6 space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Phone number</label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                    <Input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" className="pl-9 rounded-xl"/>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Referral code (optional)</label>
                  <Input data-testid="referral-input" value={referral} onChange={(e) => setReferral(e.target.value.toUpperCase())} placeholder="e.g. ABC123" className="mt-1 rounded-xl font-mono uppercase"/>
                  <div className="text-[11px] text-slate-400 mt-1">Both you and your friend get ₹100 on your first delivery.</div>
                </div>
                <label className="flex items-start gap-2 pt-1 cursor-pointer select-none" data-testid="accept-terms-label">
                  <Checkbox
                    checked={acceptTerms}
                    onCheckedChange={(v) => setAcceptTerms(!!v)}
                    data-testid="accept-terms-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-slate-600 leading-snug">
                    I have read and accept the{" "}
                    <Link to="/terms" target="_blank" className="text-slate-900 underline">Terms of Service</Link>{" "}
                    and the{" "}
                    <Link to="/privacy" target="_blank" className="text-slate-900 underline">Privacy Policy</Link>.
                  </span>
                </label>
                <Button data-testid="send-otp-btn" disabled={busy} type="submit" className="w-full rounded-full bg-amber-600 hover:bg-amber-700 mt-2">
                  {busy ? "Sending…" : "Send OTP"}
                </Button>
              </form>
            </>
          )}
          {step === 2 && (
            <>
              <h1 className="font-display text-2xl font-extrabold">Enter OTP</h1>
              <p className="text-slate-500 text-sm mt-1">Sent to <span className="font-mono">{phone}</span></p>
              {devOtp && <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2"><KeyRound className="inline w-3 h-3 mr-1"/> Dev OTP: <span className="font-mono font-bold">{devOtp}</span></div>}
              <form onSubmit={verify} className="mt-6 space-y-4">
                {isNew && (
                  <div className="grid grid-cols-2 gap-2" data-testid="signup-name-fields">
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">First name <span className="text-rose-500">*</span></label>
                      <Input data-testid="first-name-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Manveet" className="mt-1 rounded-xl"/>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Last name <span className="text-rose-500">*</span></label>
                      <Input data-testid="last-name-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Kaur" className="mt-1 rounded-xl"/>
                    </div>
                    <p className="col-span-2 text-[11px] text-slate-400">Others see you as your first name and last initial, e.g. "Manveet K."</p>
                  </div>
                )}
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i}/>)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button data-testid="verify-otp-btn" disabled={busy} type="submit" className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
                <button type="button" onClick={() => setStep(1)} className="text-sm text-slate-500 w-full">Change number</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
